function debounce (fn, ms) {
  let t
  return function debounced (...args) {
    clearTimeout(t)
    t = setTimeout(() => {
      fn.apply(this, args)
    }, ms)
  }
}

function getCardSectionUrl (handle, pdpModel) {
  const basePath = window?.Shopify?.routes?.root || '/'
  let url = `${basePath}products/${encodeURIComponent(handle)}?section_id=cart-cross-sell-card`
  if (pdpModel !== null && pdpModel !== undefined && pdpModel !== '') {
    url += `&pdp_model=${encodeURIComponent(pdpModel)}`
  }
  return url
}

async function fetchCrossSellCardHtml (handle, pdpModel) {
  const url = getCardSectionUrl(handle, pdpModel)
  const res = await fetch(url, { credentials: 'same-origin' })
  if (!res.ok) return null
  const html = await res.text()
  const temp = document.createElement('div')
  temp.innerHTML = html
  const card = temp.querySelector('[data-cart-cross-sell-card], .product-cross-sells__cross-sell-card')
  return card ? card.outerHTML : null
}

function wrapCrossSellSlide (cardHtml) {
  return `<div class="product-cross-sells__carousel-slide pl-2 shrink-0 select-none">
        <div style="width: 100%; min-width: 0;">
          ${cardHtml}
        </div>
      </div>`
}

// Mirrors the model normalisation in snippets/cart-cross-sell-card.liquid
function normaliseCrossSellModel (value) {
  if (value === null || value === undefined) return ''
  let out = String(value).replace('TentBox ', '')
  out = out.trim().toLowerCase()
  return out
}

// Cards are fetched via the Section Rendering API where the PDP model is not
// available server-side (request.params is stripped), so the server renders the
// image and price for the product's global-cheapest variant. Swap both to the
// current model's variant client-side: the image to the matching variant's
// image, and the price to the lowest-priced compatible variant (otherwise a
// Cargo PDP shows a Lite-only price - see cart-cross-sell-card.liquid).
function applyCrossSellVariantModelData (container, pdpModel) {
  const normalisedModel = normaliseCrossSellModel(pdpModel)
  if (normalisedModel === '' || !container) return

  container.querySelectorAll('[data-cart-cross-sell-card]').forEach(card => {
    const script = card.querySelector('script[data-cross-sell-variant-data]')
    if (!script) return

    let parsed
    try {
      parsed = JSON.parse(script.textContent)
    } catch (e) {
      return
    }
    if (!parsed?.variants?.length) return

    const options = Array.isArray(parsed.options) ? parsed.options : []
    const modelOptionName = parsed.modelOptionName || 'TentBox Model'
    const modelOption = options.find(o => o.name === modelOptionName || o.name === 'Model')
    if (!modelOption) return
    const position = modelOption.position

    const valueAtPosition = variant => {
      if (position === 1) return variant.option1
      if (position === 2) return variant.option2
      if (position === 3) return variant.option3
      return null
    }

    const matching = parsed.variants.filter(
      v => normaliseCrossSellModel(valueAtPosition(v)) === normalisedModel && v.available !== false,
    )
    // No compatible variant for this model - leave the server-rendered default.
    if (matching.length === 0) return

    // Image: first compatible variant that has one.
    const imageMatch = matching.find(v => v.image)
    if (imageMatch && imageMatch.image) {
      const img = card.querySelector('.product-cross-sells__cross-sell-card-image-link img')
      if (img && img.getAttribute('src') !== imageMatch.image) {
        img.setAttribute('src', imageMatch.image)
        img.removeAttribute('srcset')
        img.removeAttribute('sizes')
      }
    }

    // Price: lowest-priced compatible variant, using the server-formatted money
    // string so currency/locale formatting stays identical to tb-product-card-price.
    const cheapest = matching.reduce((a, b) => (b.price < a.price ? b : a))
    const currentEl = card.querySelector('.price-display-wrapper__current')
    if (currentEl && cheapest.priceFormatted) {
      const prefix = currentEl.querySelector('.price-display-wrapper__prefix')
      currentEl.textContent = ''
      if (prefix) currentEl.appendChild(prefix)
      currentEl.appendChild(document.createTextNode(cheapest.priceFormatted))
    }
    // Keep any compare-at price consistent with the cheapest compatible variant.
    const compareEl = card.querySelector('.price-display-wrapper__compare')
    if (compareEl) {
      if (cheapest.compareAtFormatted) {
        compareEl.textContent = cheapest.compareAtFormatted
        compareEl.style.display = ''
      } else {
        compareEl.style.display = 'none'
      }
    }
  })
}

async function refreshStaticCrossSells (root, pdpModel) {
  const handlesStr = root.dataset.crossSellHandles || ''
  const handles = handlesStr.split(',').filter(Boolean)
  const slidesContainer = root.querySelector('[data-cross-sells-slides]')
  if (!slidesContainer || handles.length === 0) return

  const cardPromises = handles.map(handle => fetchCrossSellCardHtml(handle, pdpModel))
  const cards = await Promise.all(cardPromises)
  const validCards = cards.filter(Boolean)
  if (validCards.length === 0) return

  slidesContainer.innerHTML = validCards.map(wrapCrossSellSlide).join('')
  root.dataset.pdpModel = pdpModel
  applyCrossSellVariantModelData(slidesContainer, pdpModel)

  const carousel = root.querySelector('carousel-component')
  if (carousel) {
    carousel.dispatchEvent(new CustomEvent('carousel:reinit', { bubbles: false }))
  }
}

function initCrossSellModelSync () {
  const debounced = debounce(async e => {
    const variant = e.detail?.variant
    if (!variant || !Array.isArray(variant.options)) return

    const roots = document.querySelectorAll('[data-cross-sells-source]')
    for (const root of roots) {
      const idxStr = root.dataset.pdpModelOptionIndex
      const idx = idxStr === '' || idxStr === undefined ? -1 : parseInt(idxStr, 10)
      if (idx < 0 || Number.isNaN(idx)) continue
      const pdpModel = variant.options[idx]
      if (pdpModel === null || pdpModel === undefined || pdpModel === '') continue

      const source = root.dataset.crossSellsSource
      if (source === 'static') {
        await refreshStaticCrossSells(root, pdpModel)
      } else if (source === 'complementary') {
        const el = root.closest('product-cross-sells')
        if (el && typeof el.refreshCardsForModel === 'function') {
          await el.refreshCardsForModel(pdpModel)
        }
      }
    }
  }, 200)

  document.addEventListener('variantChange', debounced)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCrossSellModelSync)
} else {
  initCrossSellModelSync()
}

class ProductCrossSells extends HTMLElement {
  connectedCallback () {
    this.productId = this.dataset.productId

    if (!this.productId) {
      console.error('[ProductCrossSells] Missing product ID')
      return
    }

    this.fetchComplementaryProducts()
  }

  async fetchComplementaryProducts () {
    try {
      const basePath = window?.Shopify?.routes?.root || '/'
      const url = `${basePath}recommendations/products.json?product_id=${encodeURIComponent(this.productId)}&limit=6&intent=complementary`

      const res = await fetch(url, { credentials: 'same-origin' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const data = await res.json()

      if (!data.products || data.products.length === 0) {
        this.hideSkeleton()
        this.style.display = 'none'
        return
      }

      const cartProductIds = this.getCartProductIds()
      const productsToShow = data.products
        .filter(p => !cartProductIds.includes(p.id))
        .slice(0, 6)

      if (productsToShow.length === 0) {
        this.hideSkeleton()
        this.style.display = 'none'
        return
      }

      this._handles = productsToShow.map(p => p.handle)
      await this.renderProducts(productsToShow)
    } catch (error) {
      console.error('[ProductCrossSells] Failed to fetch complementary products:', error)
      this.hideSkeleton()
      this.style.display = 'none'
    }
  }

  getCartProductIds () {
    try {
      const cartData = window.Shopify?.cart || {}
      return (cartData.items || []).map(item => item.product_id)
    } catch (e) {
      return []
    }
  }

  async refreshCardsForModel (pdpModel) {
    if (!this._handles || this._handles.length === 0) return

    const carousel = this.querySelector('[data-cross-sells-carousel]')
    const slidesContainer = carousel?.querySelector('[data-cross-sells-slides]')
    if (!slidesContainer) return

    this.dataset.pdpModel = pdpModel

    const cardPromises = this._handles.map(handle => fetchCrossSellCardHtml(handle, pdpModel))
    const cards = await Promise.all(cardPromises)
    const validCards = cards.filter(Boolean)

    if (validCards.length === 0) return

    slidesContainer.innerHTML = validCards.map(wrapCrossSellSlide).join('')
    applyCrossSellVariantModelData(slidesContainer, pdpModel)

    carousel.dispatchEvent(new CustomEvent('carousel:reinit', { bubbles: false }))
  }

  async renderProducts (products) {
    const carousel = this.querySelector('[data-cross-sells-carousel]')
    if (!carousel) return

    const slidesContainer = carousel.querySelector('[data-cross-sells-slides]')
    if (!slidesContainer) return

    const pdpModel = this.dataset.pdpModel || ''

    const cardPromises = products.map(product =>
      fetch(getCardSectionUrl(product.handle, pdpModel), {
        credentials: 'same-origin',
      })
        .then(res => res.text())
        .then(html => {
          const temp = document.createElement('div')
          temp.innerHTML = html
          const card = temp.querySelector('[data-cart-cross-sell-card], .product-cross-sells__cross-sell-card')
          return card ? card.outerHTML : null
        })
        .catch(() => null),
    )

    const cards = await Promise.all(cardPromises)
    const validCards = cards.filter(Boolean)

    if (validCards.length === 0) {
      this.hideSkeleton()
      this.style.display = 'none'
      return
    }

    slidesContainer.innerHTML = validCards.map(wrapCrossSellSlide).join('')
    applyCrossSellVariantModelData(slidesContainer, pdpModel)

    this.hideSkeleton()
    carousel.classList.remove('hidden')
    this.setupFaderListener(carousel)
    carousel.dispatchEvent(new CustomEvent('carousel:reinit', { bubbles: false }))
  }

  hideSkeleton () {
    const skeleton = this.querySelector('[data-skeleton]')
    if (skeleton) {
      skeleton.style.display = 'none'
    }
  }

  setupFaderListener (carousel) {
    const container = this.closest('.product-cross-sells-container')
    if (!container) return

    const fader = container.querySelector('.product-cross-sells__fader')
    if (!fader) return

    const mdBreakpoint = 768
    const mediaQuery = window.matchMedia(`(min-width: ${mdBreakpoint}px)`)

    const updateFaderVisibility = (isAtEnd) => {
      if (!mediaQuery.matches) {
        fader.style.display = ''
        return
      }
      fader.style.display = isAtEnd ? 'none' : ''
    }

    carousel.addEventListener('carousel:slidechange', (e) => {
      updateFaderVisibility(e.detail.isAtEnd)
    })

    mediaQuery.addEventListener('change', () => {
      carousel.dispatchEvent(new CustomEvent('carousel:reinit', { bubbles: false }))
    })
  }
}

if (!customElements.get('product-cross-sells')) {
  customElements.define('product-cross-sells', ProductCrossSells)
}
